import React, { useMemo } from "react";

import {
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TableRowsIcon from "@mui/icons-material/TableRows";

import {
  useGetCurrentUser,
  useGetOrganisation,
  useListUsers,
} from "../../../../store";
import { User, UserType } from "../../../../types";
import { TeamSummary } from "../../../../types.new";

import UsersCardsView from "./users-cards-view.tsx";
import UsersTableView from "./users-table-view.tsx";
import { userHasPermission } from "../../../../types-helpers";
import RemoveUserConfirmationModal from "./remove-user-confirmation-modal";
import PromoteUserConfirmationModal from "./promote-user-confirmation-modal";
import DemoteUserConfirmationModal from "./demote-user-confirmation-modal";

interface UsersSectionProps {
  teamId: string;
}

const UsersSection: React.FC<UsersSectionProps> = ({ teamId }) => {
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [activeModal, setActiveModal] = React.useState<
    "remove" | "promote" | "demote" | null
  >(null);
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [viewMode, setViewMode] = React.useState<"cards" | "list">("list");

  const { data: clientUsers, isLoading } = useListUsers();
  const { data: team } = useGetOrganisation(teamId);
  const { data: currentUser } = useGetCurrentUser();
  
  const canEdit = useMemo(() => {
    if (userHasPermission(UserType.SysAdmin, currentUser)) {
      return true;
    }

    if (!team || !currentUser) {
      return false;
    }

    return team.adminIds.includes(currentUser.userId);
  }, [team, currentUser]);

  const teamMembers: User[] = useMemo(() => {
    if (!clientUsers) {
      return [];
    }

    const teamMembers =
      clientUsers?.filter(
        (item) => item != null && item.organisationIds.includes(teamId),
      ) || [];

    return teamMembers;
  }, [clientUsers, team, teamId]);

  const closeActionModal = () => {
    setSelectedUser(null);
    setActiveModal(null);
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return teamMembers;
    }

    const query = searchQuery.toLowerCase().trim();
    return teamMembers.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query),
    );
  }, [teamMembers, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const teamSummary: TeamSummary = {
    teamId: team?.organisationId || "",
    name: team?.name || "",
    color: team?.color || "#1976d2",
  };

  return (
    <Stack direction={"column"}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <ButtonGroup
          variant="outlined"
          sx={{
            borderRadius: "0.5rem",
            "& .MuiButton-root": {
              color: "#555",
              borderColor: "#BBB",
              "&:not(:last-child)": {
                borderRight: "1px solid #BBB",
              },
            },
            "& .MuiButton-root:first-of-type": {
              borderTopLeftRadius: "0.5rem",
              borderBottomLeftRadius: "0.5rem",
            },
            "& .MuiButton-root:last-of-type": {
              borderTopRightRadius: "0.5rem",
              borderBottomRightRadius: "0.5rem",
            },
          }}
        >
          <Tooltip title="List view">
            <Button
              onClick={() => setViewMode("list")}
              aria-pressed={viewMode === "list"}
              sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
            >
              <TableRowsIcon
                sx={{
                  fontSize: 22,
                  color: viewMode === "list" ? "primary.main" : "#555",
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip title="Card view">
            <Button
              onClick={() => setViewMode("cards")}
              aria-pressed={viewMode === "cards"}
              sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
            >
              <AppsIcon
                sx={{
                  fontSize: 22,
                  color: viewMode === "cards" ? "primary.main" : "#555",
                }}
              />
            </Button>
          </Tooltip>
        </ButtonGroup>

        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search members by name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear members search"
                  onClick={() => setSearchQuery("")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            flex: 1,
            minWidth: { xs: "100%", sm: 280 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "0.5rem",
            },
          }}
        />
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <Stack
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Stack>
        ) : filteredUsers.length === 0 ? (
          <Stack
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {teamMembers.length === 0
                ? "This team has no members yet"
                : "No members found"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {teamMembers.length === 0
                ? "Add a member to get started"
                : "Try adjusting your search"}
            </Typography>
          </Stack>
        ) : viewMode === "cards" ? (
          <UsersCardsView
            team={teamSummary}
            filteredUsers={filteredUsers}
            unfilteredUsersCount={teamMembers.length}
            canEdit={canEdit}
          />
        ) : (
          <UsersTableView
            team={teamSummary}
            filteredUsers={filteredUsers}
            unfilteredUsersCount={teamMembers.length}
            canEdit={canEdit}
          />
        )}
      </Stack>

      {selectedUser && (
        <RemoveUserConfirmationModal
          organisationId={teamId}
          userId={selectedUser.userId}
          open={activeModal === "remove"}
          onClose={closeActionModal}
        />
      )}

      {selectedUser && (
        <PromoteUserConfirmationModal
          organisationId={teamId}
          userId={selectedUser.userId}
          open={activeModal === "promote"}
          onClose={closeActionModal}
        />
      )}

      {selectedUser && (
        <DemoteUserConfirmationModal
          organisationId={teamId}
          userId={selectedUser.userId}
          open={activeModal === "demote"}
          onClose={closeActionModal}
        />
      )}
    </Stack>
  );
};

export default UsersSection;
